import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { SimpleInventoryWithLinkedMaterialInfo } from '../../../store/Models/Inventories/SimpleInventory';
import { InviteSupplierModel, InviteSupplierRowModel } from '../../../store/Models/Supplier/Supplier';
import * as SelectSuppliersMaterialsStateController from '../../../store/StateControllers/Supplier/SelectSuppliersMaterialsTableStateController';
import { LoadingSpinner, LoadingSpinnerSize } from '../LoadingSpinner';
import { ProductMaterialTable } from '../ProductMaterialTable';
import { SlidingDrawer } from '../SlidingDrawer';
import { ScrollableContent, TopActionButtons, TopActionsAndScrollableContentContainer } from '../TopActionsAndScrollableContentContainer';
import { SelectingRowOptions, TableColumn, TableRow, ToxnotReactTable } from '../ToxnotReactTable';
import { InventoryOrCustomNameWithPossibleId } from '../../../store/Models/InventoryNameWithPossibleId';
import './styles.css';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../ToxnotReactModal';
import { ToxnotComponentProperties } from '../BaseApplicationComponents/ToxnotComponent';
import { ToxnotSignInRequiredComponent } from '../BaseApplicationComponents/ToxnotSignInRequiredComponent';

export interface SelectSuppliersMaterialsTableRenderOptions{
    supplierOnlyMode?: boolean; // default is false
    readOnlyMode?: boolean; // default is false
    includeActionButtons?: boolean; // default is false
    allowAddNewMaterials?: boolean; // default: true,
    lockSuppliers?: boolean; // default: false. Disables edit/add/remove of suppliers. Can still edit contact email of existing ones.
    takeFullHeightOfParent?: boolean; // defaults to true
}

interface OwnProps {
    options?: SelectSuppliersMaterialsTableRenderOptions; // when not supplied, default options are used
    initialInviteSupplerModel?: InviteSupplierModel[];
    onInviteSupplierModelDataChanged?: (model: InviteSupplierModel[]) => void
}

type SelectSuppliersMaterialsTableProps =
    ToxnotComponentProperties &
    SelectSuppliersMaterialsStateController.SelectSuppliersMaterialsTableState &
    typeof SelectSuppliersMaterialsStateController.actionCreators &
    OwnProps;

/**
 * Resuable component to enter new suppliers, select existing suppliers, select
 * existing inventories for that supplier, and add material names to create new 
 * materials. In order access the internal state of this component, the parent 
 * must the add the state models from SelectSuppliersMaterialsStateController
 * in its props.
 */
class SelectSuppliersMaterialsTable extends ToxnotSignInRequiredComponent<SelectSuppliersMaterialsTableProps> {

    // ===============================================================================

    public componentDidMount() {
        this.props.initializeSelectSuppliers(this.props.initialInviteSupplerModel);
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    public componentDidUpdate(prevProps: SelectSuppliersMaterialsTableProps) {
        if (this.props.supplierRowModels !== prevProps.supplierRowModels && this.props.onInviteSupplierModelDataChanged) {
            this.props.onInviteSupplierModelDataChanged(this.props.supplierRowModels);
        }
    }

    // ===============================================================================

    public componentWillUnmount() {
        this.props.clearSelectSuppliersState();
    }

    // ===============================================================================

    private getSlidingDrawerTitle = () => (this.props.options?.readOnlyMode ?? false) ? this.localizeString("Requested Materials") : this.localizeString("Select Materials To Request From") + " " + this.getCurrentlySelectedSupplierName();

    // ===============================================================================

    /**
     * Renders the component
     */
    public renderConcreteComponent() {
        if (this.props.isSelectingSuppliersLoading) {
            return (
                <div className="text-center">
                    <LoadingSpinner size={LoadingSpinnerSize.Medium} />
                </div>
            );
        }

        const takeFullHeightOfParent = this.props.options?.takeFullHeightOfParent ?? true;

        if (takeFullHeightOfParent) {
            return (
                <div id="invite-suppliers-table-full-content" className={takeFullHeightOfParent ? "full-height-of-parent" : ""}>
                    <TopActionsAndScrollableContentContainer tnSectionIsRaw={false} shouldMakeTnSection={false}>
                        {(this.props.options?.includeActionButtons ?? false) &&
                            <TopActionButtons>
                                {!(this.props.options?.readOnlyMode ?? false) && !(this.props.options?.lockSuppliers ?? false) && this.renderTopActionButtons()}
                            </TopActionButtons>
                        }
                        <ScrollableContent>
                            {this.renderMainTable()}
                        </ScrollableContent>
                        <div id="scroll-to-div" />
                        <SlidingDrawer
                            drawerTitle={this.getSlidingDrawerTitle()}
                            onClose={this.slidingDrawer_onClose}
                            openDrawer={this.props.materialsSelectorIsOpen}
                            customCloseButtonOptions={{ className: "btn btn-toxnot-primary", localizedText: this.localizeString("Done") }}>
                            {this.renderMaterialsSlidingDrawerContent()}
                        </SlidingDrawer>
                    </TopActionsAndScrollableContentContainer>
                </div>
            );
        }
        else {
            return (
                <div id="invite-suppliers-table-full-content" className={takeFullHeightOfParent ? "full-height-of-parent" : ""}>
                    {(this.props.options?.includeActionButtons ?? false) &&
                        <div>
                            {!(this.props.options?.readOnlyMode ?? false) && !(this.props.options?.lockSuppliers ?? false) && this.renderTopActionButtons()}
                        </div>
                    }
                    {this.renderMainTable()}
                    <div id="scroll-to-div" />
                    <SlidingDrawer
                        drawerTitle={this.getSlidingDrawerTitle()}
                        onClose={this.slidingDrawer_onClose}
                        openDrawer={this.props.materialsSelectorIsOpen}
                        customCloseButtonOptions={{ className: "btn btn-toxnot-primary", localizedText: this.localizeString("Done") }}>
                        {this.renderMaterialsSlidingDrawerContent()}
                    </SlidingDrawer>
                </div>
            );
        }
    }

    // ===============================================================================

    /**
     * Renders the top action buttons for this component.
     */
    private renderTopActionButtons() {
        return (
            <div className="top-action-buttons">
                <button type="button" id="add-supplier-btn" className="btn btn-default" onClick={e => this.addSupplierButton_click()} data-toggle="tooltip" data-placement="top" title={this.localizeString("Add a supplier row.")}><span className="glyphicon glyphicon-plus" /> {this.localizeString("Supplier Row")}</button>
                <button type="button" className="btn btn-toxnot-danger" onClick={e => this.removeSupplierRowButton_click()}><span className="glyphicon glyphicon-remove" /> { this.localizeString("Supplier")}</button>
                <button type="button" className="btn btn-default" onClick={e => this.importSuppliersButton_click()}>{this.localizeString("Import Supplier Spreadsheet")}</button>
                <input type="file" accept=".xlsx, .csv" id="import-suppliers-file-upload" style={{ display: 'none' }} data-file-type="0" onChange={this.importSuppliersFileInput_onChange} />
                <div>
                    <a href="/files/Invite%20Suppliers%20Import%20Template.xlsx">{this.localizeString("Download Import Example")}</a>
                </div>
            </div>
        );
    }

    // ===============================================================================

    /**
     * Gets all the table methods needed by the material sliding drawer table.
     * 
     * @param inventoriesToRender the inventories we are going to render in the sliding drawer table
     */
    private getSlidingDrawerTableMethods(inventoriesToRender: SimpleInventoryWithLinkedMaterialInfo[], supplierToInvite: InviteSupplierRowModel) {
        const cols: TableColumn<SimpleInventoryWithLinkedMaterialInfo>[] = [
            {
                value: this.localizeString("Name"),
                isSortable: true,
                getSortableValue: (inv: SimpleInventoryWithLinkedMaterialInfo) => inv.name
            },
            {
                value: this.localizeString("Description"),
            },
            {
                value: this.localizeString("Plant name"),
                isSortable: true,
                getSortableValue: (inv: SimpleInventoryWithLinkedMaterialInfo) => inv.plantName
            }
        ];

        const rowGenerator = (inv: SimpleInventoryWithLinkedMaterialInfo, rowIndex: number): TableRow => {
            return {
                cols: [
                    <a href={`/Data/Inventory?inventoryId=${inv.inventoryId}`} target="_blank">{inv.name}</a>,
                    inv.description,
                    inv.plantName
                ]
            };
        }

        const inventoriesTable_onRowSelected = (selectedIds: { [id: string]: string }) => {
            this.props.inventoriesSelected(selectedIds, inventoriesToRender);
        }

        const selectingOptions: SelectingRowOptions<SimpleInventoryWithLinkedMaterialInfo> | undefined = (this.props.options?.readOnlyMode ?? false)
            ? undefined
            : {
                onRowSelected: inventoriesTable_onRowSelected,
                initiallySelected: this.getInitiallySelectedForProductsMaterialTable(supplierToInvite),
                rowModelGetId: (s: SimpleInventoryWithLinkedMaterialInfo) => s.inventoryId
            };

        return {
            rowGenerator,
            cols,
            selectingOptions
        }
    }

    // ===============================================================================

    /**
     * Get the header row as an array of table columns for the sliding drawer table.
     */
    private getHeaderRowForSuppliersToInvite = () => {
        return [
            { value: this.localizeString("Company Name") },
            { value: this.localizeString("Contact Email") },
            {
                value: (
                    <>
                        {this.localizeString("Materials")}
                        {this.props.supplierRowModels.some(supplier => supplier.supplierId !== null) && !this.props.options?.readOnlyMode &&
                            <>
                                {" "}
                                <a role="button" className="add-all-suppliers-materials-button" onClick={e => this.props.selectAllMaterialsWithInventoryIdsForSelectedSuppliers()}><span className="glyphicon glyphicon-plus"></span> {this.localizeString("All Suppliers' Materials")}</a>
                            </>
                        }
                    </>
                )
            }
        ];
    }

    // ===============================================================================

    /**
     * Renders the main table view for selecting suppliers and materials
     */
    private renderMainTable() {
        const selectingRowOptions: SelectingRowOptions<InviteSupplierRowModel> | undefined = this.props.options?.readOnlyMode ? undefined : {
            rowModelGetId: (s: InviteSupplierRowModel) => s.rowIdentifier,
            onRowSelected: this.table_rowSelected,
            initiallySelected: {},
            showOnlyOnHover: true
        };

        return (
            <div id="suppliers-to-invite-container">
                <ToxnotReactTable
                    allRowModels={this.props.supplierRowModels}
                    headerRow={this.getHeaderRowForSuppliersToInvite()}
                    localizer={this.props.appServices}
                    fullSectionWidth={false}
                    enablePaging={false}
                    getRowForTable={this.getRowForSupplierToInvite}
                    selectingRowOptions={selectingRowOptions}
                />
                <datalist id="existing-suppliers-list">
                    {this.props.existingSuppliers.map(supplier => supplier.name).sort().map((supplierName, i) => (
                        <option key={i}>{supplierName}</option>
                    ))}
                </datalist>
                <ToxnotReactModal modalId={"duplicate-supplier-name-warning-modal"} localizedTitle={this.localizeString("Duplicate Suppliers Found")}>
                    <ToxnotReactModalBody>
                        <div className="alert alert-danger">{this.localizeString("You have one or more suppliers with the same name. Please make sure each supplier name is unique.")}</div>
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter localizer={this.props.appServices} customCloseButtonName={this.localizeString("Close")} />
                </ToxnotReactModal>
            </div>
        );
    }

    // ===============================================================================

    /**
     * Gets the name of the currently selected supplier. "Unknown Company" is returned
     * if there is not a currently selected supplier or if that supplier has an empty 
     * name.
     */
    private getCurrentlySelectedSupplierName() {
        const unknownCompany = this.localizeString("Unknown Company");

        if (this.props.selectedSupplierRowIdentifier === null) {
            return unknownCompany;
        }

        const supplier = SelectSuppliersMaterialsStateController.getSupplierFromRowIdentifier(this.props.selectedSupplierRowIdentifier, this.props);

        return supplier.supplierName.emptyCoalesce(unknownCompany);
    }

    // ===============================================================================

    /**
     * Renders the requested materials for a supplier as a list of filters a user can remove.
     * 
     * @param supplier the supplier whos requested materials we are going to render
     * @param showEmptyMessage show the standard empty message if the user has no requested materials
     * @param shouldTruncate show truncate when we display list of requested materials (default is 3).
     */
    private renderRequestedMaterials = (supplier: InviteSupplierRowModel, showEmptyMessage: boolean, shouldTruncate: boolean = true) => {
        const numberToDisplay: number = (shouldTruncate ? 3 : supplier.requestedMaterials.length);

        let body: JSX.Element;

        if ((supplier.requestedMaterials.length === 0) && showEmptyMessage) {
            body = (
                <p>{"Add materials below by entering new material names or selecting existing materials from your library."}</p>
            );
        }
        else {
            body = (
                <div className="active-filters-container">
                    <div className="active-filter-values-container">
                        {supplier.requestedMaterials.orderBy(r => r.name).take(numberToDisplay).map((r, i) => (
                            <div key={i} className="active-filter-value">
                                <span>{r.customName ? r.customName + " (" + r.name + ")" : r.name}</span>
                                {" "}
                                {!(this.props.options?.readOnlyMode ?? false) && <span onClick={e => this.removeRequestedMaterialButton_click(supplier.rowIdentifier, r)} className="remove-filter-value-button">x</span>}
                            </div>
                        ))}
                        {(shouldTruncate && (supplier.requestedMaterials.length > numberToDisplay)) &&
                            <a role="button" onClick={e => this.editInventoriesForSupplierButton_click(supplier)}>{this.localizeString("and") + " " + (supplier.requestedMaterials.length - numberToDisplay).toString() + " " + this.localizeString("more")}</a>
                        }
                    </div>
                </div>
            );
        }

        return (
            <div className="requested-materials-rendered-container">
                {body}
            </div>
        );
    }

    // ===============================================================================

    /**
     * Determines if the materials sliding drawer is in an a loading state, by checking if
     * theres a supplier selected, or there is a supplier selected, but their inventories have
     * not been loaded yet.
     */
    private isMaterialsSlidingDrawerContentLoading() {

        if (this.props.selectedSupplierRowIdentifier === null) {
            return true;
        }

        const supplier = SelectSuppliersMaterialsStateController.getSupplierFromRowIdentifier(this.props.selectedSupplierRowIdentifier, this.props);

        return (supplier.supplierId !== null && !this.props.supplierIdsToInventories[supplier.supplierId]);
    }

    // ===============================================================================

    /**
     * For use with the sliding drawer materials table. When the table first mounts it
     * needs to select any requested materials a supplier has.
     * 
     * @param supplier the supplier whos materials we are going to initially select.
     */
    private getInitiallySelectedForProductsMaterialTable(supplier: InviteSupplierRowModel) {
        const ret: { [id: string]: string } = {};

        for (let i of supplier.requestedMaterials) {
            if (i.inventoryId === null) {
                continue;
            }

            ret[i.inventoryId] = i.inventoryId;
        }

        return ret;
    }

    // ===============================================================================

    /**
     * Renders the material sliding drawer content.
     */
    private renderMaterialsSlidingDrawerContent() {
        if (this.isMaterialsSlidingDrawerContentLoading()) {
            return (
                <div className="text-center">
                    <LoadingSpinner size={LoadingSpinnerSize.Medium} />
                </div>
            );
        }

        if (this.props.selectedSupplierRowIdentifier === null) {
            throw new Error("Selected Supplier Row Identifier cannot be null here.");
        }

        const supplierToInvite = SelectSuppliersMaterialsStateController.getSupplierFromRowIdentifier(this.props.selectedSupplierRowIdentifier, this.props);

        const inventoriesToRender = supplierToInvite.supplierId === null ? [] : this.props.supplierIdsToInventories[supplierToInvite.supplierId];
        const slidingDrawerTableMethods = this.getSlidingDrawerTableMethods(inventoriesToRender, supplierToInvite);

        if (!supplierToInvite) {
            throw new Error("Existing Supplier Could not be found");
        }

        const showFreeFormTextBoxUi = (supplierToInvite.supplierId === null) && (supplierToInvite.requestedMaterials.length === 0);

        return (
            <div id="invite-supplier-sliding-drawer-full-content">
                {!showFreeFormTextBoxUi &&
                    this.renderRequestedMaterials(supplierToInvite, true, false)
                }
                {!(this.props.options?.readOnlyMode ?? false) &&
                    <div className="grouped-data">
                        {(this.props.options?.allowAddNewMaterials ?? true) &&
                            <>
                                {showFreeFormTextBoxUi
                                    ? (
                                        <div>
                                            <p>{this.localizeString("Type or paste your Material names, separated by commas:")}</p>
                                            <textarea className="form-control" rows={10} value={this.props.newMaterialsTextBoxValue} onChange={e => this.props.updateSelectSuppliersState({ newMaterialsTextBoxValue: e.target.value })}></textarea>
                                        </div>
                                    )
                                    : (
                                        <div className="group">
                                            <div className="title">
                                                {this.localizeString("Ask for new Materials")}
                                            </div>
                                            <div className="content">
                                                {this.renderRequestedDataInputForm(supplierToInvite)}
                                            </div>
                                        </div>
                                    )
                                }
                            </>
                        }
                        {(supplierToInvite.supplierId !== null) &&
                            <div className="group">
                                <div className="title">
                                    {this.localizeString("Existing Materials/Products")}
                                </div>
                                <div className="content">
                                    <div id="sliding-drawer-materials-table-container">
                                        {inventoriesToRender.length > 0
                                            ? <ProductMaterialTable
                                                allRowModels={inventoriesToRender}
                                                getRowForTable={slidingDrawerTableMethods.rowGenerator}
                                                headerRow={slidingDrawerTableMethods.cols}
                                                localizer={this.props.appServices}
                                                selectingRowOptions={slidingDrawerTableMethods.selectingOptions}
                                            />
                                            :
                                            <>
                                                <p>
                                                    {this.localizeString("Doesn't look like you have any materials in your library yet for this supplier. No worry! You can still enter part names in the above field.")}
                                                </p>
                                                <p>
                                                    {this.localizeString("The part names you enter will automatically be added in your library for that supplier when you send your requests.")}
                                                </p>
                                            </>
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                    </div>}
            </div>
        );
    }

    // ===============================================================================

    private renderNoMaterialsChosenMessage = () => (
        <div className="no-materials-selected-message">
            <div>
                {this.localizeString("None Selected. Your supplier will choose the materials they respond with.")}
            </div>
        </div>
    );

    // ===============================================================================

    /**
     * Renders the requested data column for a supplier in the main table.
     * 
     * @param supplier the supplier who's row and materials we are rendering.
     */
    private renderRequestedDataColumn = (supplier: InviteSupplierRowModel): JSX.Element => {
        return (
            <div className="requested-data-column" >
                {(this.props.options?.supplierOnlyMode ?? false)
                    ? (
                        <div>{this.localizeString("Only Requesting Data on Suppliers.")}</div>
                    )
                    : (
                        <>
                            <div className="edit-materials-button-container">
                                <button type="button" className="btn btn-default add-materials-for-existing-supplier-btn" onClick={e => this.editInventoriesForSupplierButton_click(supplier)}>
                                    {(this.props.options?.readOnlyMode ?? false)
                                        ? this.localizeString("View Materials")
                                        : (
                                            <>
                                                {(supplier.requestedMaterials.length === 0)
                                                    ? (
                                                        <>
                                                            <span className="glyphicon glyphicon-plus" />
                                                            {(" " + this.localizeString("Materials"))}
                                                        </>
                                                    )
                                                    : (
                                                        <span className="glyphicon glyphicon-pencil" />
                                                    )
                                                }

                                            </>
                                        )
                                    }
                                </button>
                            </div>
                            {supplier.requestedMaterials.length > 0 ? this.renderRequestedMaterials(supplier, false) : this.renderNoMaterialsChosenMessage()}
                        </>
                    )}
            </div>
        );
    }

    // ===============================================================================

    /**
     * Renders the request data input form used in the material sliding drawer.
     * 
     * @param supplier the supplier whom we are rendering the input form for.
     */
    private renderRequestedDataInputForm(supplier: InviteSupplierRowModel) {
        return (
            <div className="add-requested-material-input-field">
                <div className="input-group">
                    <input
                        type="text"
                        className="form-control new-materials-name-input"
                        placeholder={this.localizeString("Type Part Name. Hit Enter Key to Add.")}
                        onKeyDown={e => this.addRequestedMaterial_onKeyDown(e, supplier)}
                        disabled={(this.props.options?.readOnlyMode ?? false)}
                    />
                    <span className="input-group-btn">
                        <button type="button" className="btn btn-default" onClick={e => this.addRequestedMaterialButton_click(supplier)}>{this.localizeString("Add")}</button>
                    </span>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderSupplierContactEmailDropdown = (supplierId: string, rowId: string, isReadonly?: boolean) => {
        const supplier = this.props.existingSuppliersTable[supplierId];
        const contactList = supplier?.contactList ?? [];

        if (contactList.length === 0) {
            return null;
        }

        return (
            <div className="input-group-btn">
                <button type="button" className="btn btn-default dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" disabled={isReadonly}>
                    <span className="caret"></span>
                </button>
                <ul className="dropdown-menu dropdown-menu-right">
                    {[...contactList].sort((a, b) => a.email.localeCompare(b.email)).map((contact, i) =>
                        <li key={i} onClick={e => this.suppliersEmail_onClick(rowId, contact.email)}><a role="button">{`${contact.email} ${contact.label !== '' ? `(${contact.label})` : ''}`}</a></li>
                    )}
                </ul>
            </div>
        );
    };

    // ===============================================================================

    /**
     * Gets the row for a supplier as a list of columns.
     * 
     * @param supplier the supplier we are getting the row for
     * @param rowIndex the row index of the current row we are getting.
     */
    private getRowForSupplierToInvite = (supplier: InviteSupplierRowModel, rowIndex: number): TableRow => {
        return {
            cols: [
                <div className="supplier-input-field">
                    {(this.props.options?.lockSuppliers ?? false) ?
                        <p>{supplier.supplierName}</p> :
                        <input
                            type="text"
                            list={"existing-suppliers-list"}
                            onChange={e => this.supplierNameInputForm_onChange(e, supplier.rowIdentifier)}
                            className={"supplier-name-input form-control" + (supplier.supplierName.trim() === "" ? " empty-input" : "")}
                            value={supplier.supplierName}
                            autoFocus={rowIndex === this.props.supplierRowModels.length - 1}
                            placeholder={this.localizeString("Supplier 1")}
                            disabled={(this.props.options?.readOnlyMode ?? false) || (this.props.options?.lockSuppliers ?? false)}
                        />
                    }
                </div>,
                <div className={"supplier-input-field" + (supplier.supplierId !== null ? " input-group" : "" )}>
                    <input
                        type="text"
                        className={"email-input form-control" + (supplier.contactEmail.trim() === "" ? " empty-input" : "")}
                        placeholder={this.localizeString("supplier1@suppliercompany.com")}
                        onChange={e => this.props.updateSupplierToInvite(supplier.rowIdentifier, {contactEmail: e.target.value})}
                        value={supplier.contactEmail}
                        disabled={(this.props.options?.readOnlyMode ?? false)}
                    />
                    {supplier.supplierId !== null && this.props.existingSuppliersTable[supplier.supplierId] && this.renderSupplierContactEmailDropdown(supplier.supplierId, supplier.rowIdentifier, this.props.options?.readOnlyMode)}
                </div>,
                this.renderRequestedDataColumn(supplier)
            ]
        }
    };

    // ===============================================================================

    /**
     * Callback passed into the main table that gets called when a supplier has been
     * selected.
     * 
     * @param selectedIds the row identifiers of the suppliers selected.
     */
    private table_rowSelected = (selectedIds: { [id: string]: string }) => {
        this.props.updateSelectSuppliersState({
            selectedSupplierRowIds: { ...selectedIds }
        });
    }

    // ===============================================================================
    //
    // UI Events
    //

    private addSupplierButton_click = () => {
        this.props.addSupplierToInvite();
    }

    // ===============================================================================

    private addNewRequestedMaterial(supplier: InviteSupplierRowModel) {
        const inputForm = $('#invite-supplier-sliding-drawer-full-content .new-materials-name-input');
        const inputVal = inputForm?.val()?.toString() ?? "";

        this.props.addRequestedMaterial(inputVal, supplier);

        // clear input after submit
        inputForm?.val("");
    }

    private addRequestedMaterial_onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, supplier: InviteSupplierRowModel) => {
        if (e.key !== "Enter") {
            return;
        }

        this.addNewRequestedMaterial(supplier);
    }

    // ===============================================================================

    private addRequestedMaterialButton_click = (supplier: InviteSupplierRowModel) => {
        this.addNewRequestedMaterial(supplier);
    }

    // ===============================================================================

    private suppliersEmail_onClick = (rowIdentifer: string, email: string) => {
        this.props.updateSupplierToInvite(rowIdentifer, {contactEmail: email});
    }

    // ===============================================================================

    private importSuppliersFileInput_onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.importSuppliers(e);
    }

    // ===============================================================================

    private importSuppliersButton_click = () => {
        $('#import-suppliers-file-upload').click();
    }

    // ===============================================================================

    private supplierNameInputForm_onChange = (e: React.ChangeEvent<HTMLInputElement>, rowIdentifier: string) => {
        const existingSupplier = SelectSuppliersMaterialsStateController.getExistingSupplierFromName(e.target.value, this.props.existingSuppliers);

        if (existingSupplier !== null) {
            this.props.addExistingSuppliersToSupplierToInvite(rowIdentifier, existingSupplier);
        }
        else {
            this.props.updateSupplierToInvite(rowIdentifier, { supplierName: e.target.value }, true);
        }
    }

    // ===============================================================================

    private slidingDrawer_onClose = () => {
        const supplier = this.props.supplierRowModels.firstOrDefault(s => s.rowIdentifier === this.props.selectedSupplierRowIdentifier);

        if (supplier === null) {
            throw new Error("Supplier not found");
        }

        if ((supplier.supplierId === null) && (supplier.requestedMaterials.length === 0)) {
            const newMaterialNames = this.props.newMaterialsTextBoxValue.trim().split(',').map(s => s.trim()).filter(s => s.length > 0);

            this.props.updateSupplierToInvite(
                supplier.rowIdentifier,
                {
                    requestedMaterials: newMaterialNames.map(name => ({
                        inventoryId: null,
                        name: name,
                        customName: null
                    }))
                });
        }

        this.props.updateSelectSuppliersState({
            selectedSupplierRowIdentifier: null,
            materialsSelectorIsOpen: false,
            newMaterialsTextBoxValue: ""
        });
    }

    // ===============================================================================

    private editInventoriesForSupplierButton_click = (supplier: InviteSupplierRowModel) => {
        // a supplier that doesn't exist yet in the user's library, and has no materials,
        // gets a simple low friction text box to add material names
        // otherwise we open the general materials editor

        if ((supplier.supplierId === null) && (supplier.requestedMaterials.length === 0)) {
            this.props.updateSelectSuppliersState({
                materialsSelectorIsOpen: true,
                selectedSupplierRowIdentifier: supplier.rowIdentifier,
                newMaterialsTextBoxValue: ""
            });
        }
        else {
            this.props.updateSelectSuppliersState({
                materialsSelectorIsOpen: true,
                selectedSupplierRowIdentifier: supplier.rowIdentifier,
                newMaterialsTextBoxValue: ""
            });
        }
    }

    // ===============================================================================

    private removeRequestedMaterialButton_click = (rowId: string, materialToRemove: InventoryOrCustomNameWithPossibleId) => {
        this.props.removeRequestedMaterial(rowId, materialToRemove);
    }

    // ===============================================================================

    private removeSupplierRowButton_click = () => {
        this.props.removeSuppliersToInvite();
    }
}

export default connect(
    (state: ApplicationState, ownProps: OwnProps) => ({
        ...state.selectSuppliersMaterialsTableState,
        appServices: state.appServices,
        ...ownProps
    }),
    SelectSuppliersMaterialsStateController.actionCreators // Selects which action creators are merged into the component's props
)(SelectSuppliersMaterialsTable as any)
